import React, { useState, useRef, useEffect } from 'react';
import { Send, X, Zap } from 'lucide-react';
import { chatWithDuck } from '../../services/geminiService';
import { useRecentSignals } from '../../hooks/useDuckSignals';
import { formatEther } from 'viem';

interface ChatMessage {
    role: 'user' | 'model';
    text: string;
    timestamp: number;
}

const ChatBot: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([
        {
            role: 'model',
            text: "Hello. I am the Duckmon Strategic AI. I monitor the Monad blockchain for precise trading signals. How can I assist you with your analysis today?",
            timestamp: Date.now()
        }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hasUnread, setHasUnread] = useState(true);

    // Blockchain Data
    const { signals } = useRecentSignals(1);
    const lastSignal = signals && signals[0];

    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isOpen]);

    // Notify user about new signals
    useEffect(() => {
        if (lastSignal && !isOpen) {
            setHasUnread(true);
        }
    }, [lastSignal, isOpen]);

    const handleSend = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!input.trim() || isLoading) return;

        const userMsg: ChatMessage = { role: 'user', text: input, timestamp: Date.now() };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setIsLoading(true);

        // Inject latest signal context if available
        let context = "";
        if (lastSignal) {
            context = `[Latest Market Info: Signal ${lastSignal.signalType} at ${formatEther(lastSignal.price)} MON. Reason: ${lastSignal.reason}. Confidence: ${lastSignal.confidence}%] `;
        }

        const responseText = await chatWithDuck(context + input);

        const botMsg: ChatMessage = { role: 'model', text: responseText, timestamp: Date.now() };
        setMessages(prev => [...prev, botMsg]);
        setIsLoading(false);
    };

    return (
        <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>

            {/* Signal Notification Bubble */}
            {!isOpen && hasUnread && lastSignal && (
                <div className="animate-bounce-in" style={{
                    marginBottom: '10px',
                    background: 'rgba(94, 23, 235, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid var(--monad-light)',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    borderBottomRightRadius: '4px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                    maxWidth: '240px',
                    cursor: 'pointer'
                }} onClick={() => { setIsOpen(true); setHasUnread(false); }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <Zap size={14} className="text-yellow-400" />
                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: 'var(--monad-light)', textTransform: 'uppercase' }}>New Signal Detected</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '12px', fontWeight: '500', color: '#fff' }}>
                        Agent just posted a <span style={{ color: lastSignal.signalType === 'BUY' ? '#4ade80' : '#f87171' }}>{lastSignal.signalType}</span> signal!
                    </p>
                </div>
            )}

            {/* Chat Window */}
            {isOpen && (
                <div className="glass-card animate-float-up" style={{
                    width: '350px',
                    height: '500px',
                    marginBottom: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                    border: '1px solid var(--duck-yellow)'
                }}>
                    {/* Header */}
                    <div style={{
                        padding: '16px',
                        background: 'rgba(255, 215, 0, 0.1)',
                        borderBottom: '1px solid var(--glass-border)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ position: 'relative' }}>
                                <img src="/images/duck.png" alt="Bot" style={{ width: '32px', height: '32px' }} />
                                <span style={{ position: 'absolute', bottom: 0, right: 0, width: '8px', height: '8px', background: '#22c55e', borderRadius: '50%', border: '1px solid black' }}></span>
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', fontFamily: 'var(--font-meme)', color: 'var(--duck-yellow)' }}>Duck Oracle</h3>
                            </div>
                        </div>
                        <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--white)', cursor: 'pointer' }}>
                            <X size={20} />
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="scrollbar-hide" style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {messages.map((msg, idx) => (
                            <div key={idx} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                                <div style={{
                                    maxWidth: '85%',
                                    padding: '10px 14px',
                                    borderRadius: '12px',
                                    fontSize: '13px',
                                    lineHeight: '1.4',
                                    borderTopLeftRadius: msg.role === 'user' ? '12px' : '2px',
                                    borderTopRightRadius: msg.role === 'user' ? '2px' : '12px',
                                    background: msg.role === 'user' ? 'var(--white)' : 'rgba(255, 255, 255, 0.1)',
                                    color: msg.role === 'user' ? 'var(--black)' : 'var(--white)',
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                                }}>
                                    {msg.text}
                                </div>
                            </div>
                        ))}
                        {isLoading && (
                            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <div style={{
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    padding: '10px 14px',
                                    borderRadius: '12px',
                                    borderTopLeftRadius: '2px'
                                }}>
                                    <span className="animate-pulse">Thinking... Quack?</span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <form onSubmit={handleSend} style={{ padding: '12px', borderTop: '1px solid var(--glass-border)', background: 'rgba(0,0,0,0.2)', display: 'flex', gap: '8px' }}>
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ask me anything..."
                            style={{
                                flex: 1,
                                background: 'rgba(0,0,0,0.3)',
                                border: '1px solid var(--glass-border)',
                                color: 'white',
                                borderRadius: '20px',
                                padding: '8px 16px',
                                fontSize: '13px',
                                outline: 'none'
                            }}
                        />
                        <button type="submit" disabled={isLoading} style={{
                            background: 'var(--duck-yellow)',
                            color: 'black',
                            border: 'none',
                            borderRadius: '50%',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            opacity: isLoading ? 0.7 : 1
                        }}>
                            <Send size={16} />
                        </button>
                    </form>
                </div>
            )}

            {/* Launcher Button */}
            {!isOpen && (
                <button
                    onClick={() => { setIsOpen(true); setHasUnread(false); }}
                    className="animate-float"
                    style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, var(--duck-yellow), var(--duck-orange))',
                        border: '4px solid rgba(255,255,255,0.2)',
                        boxShadow: '0 0 20px var(--duck-glow)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.2s',
                        zIndex: 10000
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                    <img src="/images/duck.png" alt="Chat" style={{ width: '40px', height: '40px' }} />
                    {hasUnread && (
                        <span style={{
                            position: 'absolute',
                            top: '0',
                            right: '0',
                            width: '16px',
                            height: '16px',
                            background: 'red',
                            borderRadius: '50%',
                            border: '2px solid #fff'
                        }}></span>
                    )}
                </button>
            )}
        </div>
    );
};

export default ChatBot;
